import {
  createContext,
  ReactNode,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { CatalogApi } from '@backstage/catalog-client';
import {
  ErrorApi,
  errorApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { InsightEntity } from '@internal/backstage-plugin-catalog-devops-common';

export const EXTERNAL_ACCESS_ANALYZER_TYPE = 'ExternalAccess';
const GLOBAL_REGION = 'global';

interface InsightsContextProviderProps {
  children: ReactNode;
  entityName: string;
}

enum ActionTypes {
  LOAD_COMPLETED = 'LOAD_COMPLETED',
  SELECT_REGIONS = 'SELECT_REGIONS',
}

type LoadCompletedAction = {
  type: ActionTypes.LOAD_COMPLETED;
  payload: InsightEntity[];
};

type SelectRegionsAction = {
  type: ActionTypes.SELECT_REGIONS;
  payload: string[];
};

type InsightsAction = LoadCompletedAction | SelectRegionsAction;

type InsightsState = {
  allInsights: InsightEntity[];
  allRegions: string[];
  selectedRegions: string[];
  status: string;
};

type InsightsContextType = {
  allInsights: InsightEntity[];
  globalInsights: InsightEntity[];
  regionalInsights: InsightEntity[];
  allRegions: string[];
  selectedRegions: string[];
  isLoading: boolean;
  setSelectedRegions: (regions: string[]) => void;
};

const initialInsightsState: InsightsState = {
  allInsights: [],
  allRegions: [],
  selectedRegions: [],
  status: 'initial_load',
};

export const InsightsContext = createContext<InsightsContextType | null>(null);

export function InsightsContextProvider(
  props: Readonly<InsightsContextProviderProps>,
) {
  const catalogApi = useApi<CatalogApi>(catalogApiRef);
  const errorApi = useApi<ErrorApi>(errorApiRef);

  const insightsReducer = (
    state: InsightsState,
    action: InsightsAction,
  ): InsightsState => {
    switch (action.type) {
      case ActionTypes.LOAD_COMPLETED: {
        const stateCopy = { ...state };
        stateCopy.allInsights = action.payload;
        stateCopy.allRegions = action.payload
          .map(i => i.spec.region)
          .filter(
            (region, index, allRegions) =>
              region !== GLOBAL_REGION && allRegions.indexOf(region) === index,
          );
        stateCopy.status = 'wait';
        return stateCopy;
      }
      case ActionTypes.SELECT_REGIONS: {
        return {
          ...state,
          selectedRegions: action.payload,
        };
      }
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(insightsReducer, initialInsightsState);

  useEffect(() => {
    let cancelled = false;
    catalogApi
      .getEntities({
        filter: {
          kind: 'insight',
          'spec.type': EXTERNAL_ACCESS_ANALYZER_TYPE,
          'spec.account': props.entityName,
        },
      })
      .then(value => {
        if (cancelled) {
          return;
        }
        dispatch({
          type: ActionTypes.LOAD_COMPLETED,
          payload: value.items as InsightEntity[],
        });
      })
      .catch(error => {
        if (cancelled) {
          return;
        }
        // A rejected catalog request must still leave the terminal state,
        // otherwise the insights block spins forever with no error message.
        errorApi.post(error as Error);
        dispatch({ type: ActionTypes.LOAD_COMPLETED, payload: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi, errorApi, props.entityName]);

  const setSelectedRegions = (regions: string[]) =>
    dispatch({ type: ActionTypes.SELECT_REGIONS, payload: regions });

  const context = useMemo(
    () => ({
      allInsights: state.allInsights,
      globalInsights: state.allInsights.filter(
        insight => insight.spec.region === GLOBAL_REGION,
      ),
      regionalInsights: state.allInsights.filter(i => {
        if (i.spec.region === GLOBAL_REGION) return false;
        if (state.selectedRegions.length === 0) return true;
        return state.selectedRegions.includes(i.spec.region);
      }),
      allRegions: state.allRegions,
      selectedRegions: state.selectedRegions,
      isLoading: state.status === 'initial_load',
      setSelectedRegions,
    }),
    [state.allInsights, state.allRegions, state.selectedRegions, state.status],
  );

  return (
    <InsightsContext.Provider value={context}>
      {props.children}
    </InsightsContext.Provider>
  );
}
